export type NcrSourceType = 'ACCEPTANCE' | 'MID' | 'FINAL' | 'OTHER';
export type NcrDisposition = 'REWORK' | 'RETURN' | 'SCRAP' | 'USE_AS_IS' | 'OTHER';
export type NcrStatus = 'DRAFT' | 'OPEN' | 'CLOSED' | 'APPROVED';

/** 불량 유형 — 파레토 차트용 */
export type NcrDefectCategory =
  | 'APPEARANCE'
  | 'DIMENSION'
  | 'FUNCTION'
  | 'ELECTRICAL'
  | 'PACKAGING'
  | 'LABEL'
  | 'CONTAMINATION'
  | 'DOCUMENT'
  | 'OTHER';

/** 원인 분류 — 특성요인도(5M1E)용 */
export type NcrCauseCategory =
  | 'MAN'
  | 'MACHINE'
  | 'MATERIAL'
  | 'METHOD'
  | 'MEASUREMENT'
  | 'ENVIRONMENT'
  | 'OTHER';

export interface NonconformanceStockLine {
  lineId?: number | null;
  inventoryId: number;
  itemName?: string | null;
  specText?: string | null;
  lotNo?: string | null;
  qty: string;
  remark?: string | null;
  sortOrder?: number | null;
}

export interface NonconformanceStockLinePayload {
  inventoryId: number;
  itemName?: string | null;
  specText?: string | null;
  lotNo?: string | null;
  qty: string;
  remark?: string | null;
}

export interface NonconformanceReport {
  reportId: number;
  reportNo?: string | null;
  reportDate?: string | null;
  sourceType?: NcrSourceType | string | null;
  sourceInspectionId?: number | null;
  inventoryId?: number | null;
  itemName?: string | null;
  specText?: string | null;
  lotNo?: string | null;
  qty?: string | null;
  defectCategory?: NcrDefectCategory | string | null;
  causeCategory?: NcrCauseCategory | string | null;
  defectDesc?: string | null;
  cause?: string | null;
  disposition?: NcrDisposition | string | null;
  correctiveAction?: string | null;
  status?: NcrStatus | string | null;
  reporterName?: string | null;
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
  approverUserId?: number | null;
  canRevoke?: boolean | null;
  approvalInProgress?: boolean | null;
  pendingApprovalRequestId?: number | null;
  canCancelApproval?: boolean | null;
  closedAt?: string | null;
  remark?: string | null;
  contractId?: number | null;
  stockLines?: NonconformanceStockLine[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface NonconformanceReportPayload {
  reportNo?: string | null;
  reportDate?: string | null;
  sourceType?: NcrSourceType | string;
  sourceInspectionId?: number | null;
  inventoryId?: number | null;
  itemName: string;
  specText?: string | null;
  lotNo?: string | null;
  qty?: string | null;
  defectCategory?: NcrDefectCategory | string | null;
  causeCategory?: NcrCauseCategory | string | null;
  defectDesc?: string | null;
  cause?: string | null;
  disposition?: NcrDisposition | string | null;
  correctiveAction?: string | null;
  status?: NcrStatus | string;
  reporterName?: string | null;
  remark?: string | null;
  contractId?: number | null;
  stockLines?: NonconformanceStockLinePayload[] | null;
}

export const NCR_SOURCE_OPTIONS: { value: NcrSourceType; label: string }[] = [
  { value: 'ACCEPTANCE', label: '인수검사' },
  { value: 'MID', label: '중간검사' },
  { value: 'FINAL', label: '제품검사' },
  { value: 'OTHER', label: '기타' },
];

export const NCR_DISPOSITION_OPTIONS: { value: NcrDisposition; label: string }[] = [
  { value: 'REWORK', label: '재작업' },
  { value: 'RETURN', label: '반품' },
  { value: 'SCRAP', label: '폐기' },
  { value: 'USE_AS_IS', label: '특채(그대로 사용)' },
  { value: 'OTHER', label: '기타' },
];

export const NCR_DEFECT_CATEGORY_OPTIONS: { value: NcrDefectCategory; label: string }[] = [
  { value: 'APPEARANCE', label: '외관' },
  { value: 'DIMENSION', label: '치수' },
  { value: 'FUNCTION', label: '기능·성능' },
  { value: 'ELECTRICAL', label: '전기·회로' },
  { value: 'PACKAGING', label: '포장' },
  { value: 'LABEL', label: '라벨·식별' },
  { value: 'CONTAMINATION', label: '오염·이물' },
  { value: 'DOCUMENT', label: '문서·성적' },
  { value: 'OTHER', label: '기타' },
];

export const NCR_CAUSE_CATEGORY_OPTIONS: { value: NcrCauseCategory; label: string }[] = [
  { value: 'MAN', label: '사람(Man)' },
  { value: 'MACHINE', label: '설비(Machine)' },
  { value: 'MATERIAL', label: '재료(Material)' },
  { value: 'METHOD', label: '방법(Method)' },
  { value: 'MEASUREMENT', label: '측정(Measurement)' },
  { value: 'ENVIRONMENT', label: '환경(Environment)' },
  { value: 'OTHER', label: '기타' },
];

export function ncrSourceLabel(value?: string | null) {
  return NCR_SOURCE_OPTIONS.find((o) => o.value === value)?.label || value || '-';
}

export function ncrDispositionLabel(value?: string | null) {
  return NCR_DISPOSITION_OPTIONS.find((o) => o.value === value)?.label || value || '-';
}

export function ncrDefectCategoryLabel(value?: string | null) {
  return NCR_DEFECT_CATEGORY_OPTIONS.find((o) => o.value === value)?.label || value || '-';
}

export function ncrCauseCategoryLabel(value?: string | null) {
  return NCR_CAUSE_CATEGORY_OPTIONS.find((o) => o.value === value)?.label || value || '-';
}

export function ncrStatusLabel(
  value?: string | null,
  approvalInProgress?: boolean | null
) {
  if (approvalInProgress) return '결재 요청 중';
  switch (value) {
    case 'DRAFT':
      return '작성중';
    case 'OPEN':
      return '진행중';
    case 'CLOSED':
      return '종결';
    case 'APPROVED':
      return '승인';
    default:
      return value || '-';
  }
}

/** 검사 상세 → 부적합품 보고서 등록 시 넘기는 초기값 */
export interface NcrCreatePrefill {
  sourceType: NcrSourceType;
  sourceInspectionId: number;
  inventoryId?: number | null;
  itemName?: string;
  specText?: string;
  lotNo?: string;
  qty?: string;
  defectCategory?: NcrDefectCategory | string;
  causeCategory?: NcrCauseCategory | string;
  defectDesc?: string;
  reporterName?: string;
  reportDate?: string;
}

export function ncrAffectsInventory(disposition?: string | null) {
  return disposition === 'SCRAP' || disposition === 'RETURN';
}

export function hasNonconformance(
  overallJudgment?: string | null,
  items?: { judgment?: string | null }[] | null
) {
  if ((overallJudgment ?? '').trim() === '부적합') return true;
  return (items ?? []).some((item) => (item.judgment ?? '').trim() === '부적합');
}

export function buildNcrDefectDesc(params: {
  sourceLabel: string;
  inspectionId: number;
  overallJudgment?: string | null;
  items?: { itemName?: string | null; judgment?: string | null }[] | null;
  remark?: string | null;
}) {
  const lines: string[] = [
    `${params.sourceLabel} #${params.inspectionId} 에서 부적합이 확인되어 작성`,
  ];
  if ((params.overallJudgment ?? '').trim()) {
    lines.push(`종합 판정: ${params.overallJudgment}`);
  }
  const failed = (params.items ?? []).filter((item) => (item.judgment ?? '').trim() === '부적합');
  if (failed.length > 0) {
    lines.push('부적합 항목:');
    failed.forEach((item) => {
      lines.push(`- ${item.itemName || '항목'}`);
    });
  }
  if (params.remark?.trim()) {
    lines.push(`비고: ${params.remark.trim()}`);
  }
  return lines.join('\n');
}
